<script setup>
import { formatMarketCap } from '~/utils/utils.js';
import Card from '~/components/Card.vue';
import InfoTooltip from '~/components/InfoTooltip.vue';
import LoadingSkeleton from '~/components/LoadingSkeleton.vue';
import CardLabel from '~/components/CardLabel.vue';

// Props
const props = defineProps({
    marketCap: { type: Number, default: 0 },
    marketCapRank: { type: Number, default: 0 },
    volume24h: { type: Number, default: 0 },
    fullyDilutedValuation: { type: Number, default: 0 },
    loading: { type: Boolean, default: false }
});
</script>

<template>
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 cards-gap">
        <!-- Market Cap -->
        <Card>
            <div class="flex items-center space-x-4">
                <div class="p-3 bg-indigo-50 dark:bg-indigo-900/50 rounded-xl">
                    <svg class="w-6 h-6 text-indigo-500 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <div>
                    <div class="flex items-center">
                        <CardLabel text="Market Cap" />
                        <InfoTooltip text="Total market capitalization of KAS in USD" />
                    </div>
                    <div class="mt-1">
                        <LoadingSkeleton v-if="props.loading" height="28px" width="90px" />
                        <p v-else class="text-xl font-bold text-gray-900 dark:text-white slide-in-up">${{ formatMarketCap(props.marketCap) }}</p>
                    </div>
                </div>
            </div>
        </Card>

        <!-- Market Cap Rank -->
        <Card>
            <div class="flex items-center space-x-4">
                <div class="p-3 bg-yellow-50 dark:bg-yellow-900/50 rounded-xl">
                    <svg class="w-6 h-6 text-yellow-500 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11l5-5m0 0l5 5m-5-5v12" />
                    </svg>
                </div>
                <div>
                    <div class="flex items-center">
                        <CardLabel text="Market Cap Rank" />
                        <InfoTooltip text="Rank of KAS based on its market capitalization" />
                    </div>
                    <div class="mt-1">
                        <LoadingSkeleton v-if="props.loading" height="28px" width="90px" />
                        <p v-else class="text-xl font-bold text-gray-900 dark:text-white slide-in-up">#{{ props.marketCapRank || 0 }}</p>
                    </div>
                </div>
            </div>
        </Card>

        <!-- 24h Volume -->
        <Card>
            <div class="flex items-center space-x-4">
                <div class="p-3 bg-green-50 dark:bg-green-900/50 rounded-xl">
                    <svg class="w-6 h-6 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                </div>
                <div>
                    <div class="flex items-center">
                        <CardLabel text="24h Volume" />
                        <InfoTooltip text="Total trading volume of KAS in the last 24 hours" />
                    </div>
                    <div class="mt-1">
                        <LoadingSkeleton v-if="props.loading" height="28px" width="90px" />
                        <p v-else class="text-xl font-bold text-gray-900 dark:text-white slide-in-up">${{ formatMarketCap(props.volume24h) }}</p>
                    </div>
                </div>
            </div>
        </Card>

        <!-- Fully Diluted Valuation -->
        <Card>
            <div class="flex items-center space-x-4">
                <div class="p-3 bg-purple-50 dark:bg-purple-900/50 rounded-xl">
                    <svg class="w-6 h-6 text-purple-500 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                </div>
                <div>
                    <div class="flex items-center">
                        <CardLabel text="Fully Diluted Market Cap" />
                        <InfoTooltip text="Market capitalization if the maximum supply of KAS is in circulation" />
                    </div>
                    <div class="mt-1">
                        <LoadingSkeleton v-if="props.loading" height="28px" width="90px" />
                        <p v-else class="text-xl font-bold text-gray-900 dark:text-white slide-in-up">${{ formatMarketCap(props.fullyDilutedValuation) }}</p>
                    </div>
                </div>
            </div>
        </Card>
    </div>
</template>